import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPalette } from '../themePalette';

const API_ROOT = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = API_ROOT + '/api';

const resolveUrl = (u) => (u && u.startsWith('http')) ? u : `${API_ROOT}${u}`;

// Pick a project's dashboard cover: upload a new photo OR choose one already
// attached to the project's items (a real room / product photo in the app).
const CoverPicker = ({ project, theme = 'dark', busy = false, onClose, onCoverUpload, onCoverSet }) => {
  const T = getPalette(theme);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/projects/${project.id}/photos`);
        const data = await res.json();
        if (alive) setPhotos(data.photos || []);
      } catch (e) {
        if (alive) setPhotos([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [project.id]);

  const pick = (url) => {
    if (onCoverSet) { onCoverSet(project, url); onClose(); }
  };

  return createPortal(
    <div data-testid="cover-picker-overlay" onClick={onClose} className="fixed inset-0 z-[100000] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.72)' }}>
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl" style={{ background: T.card, border: `1px solid ${T.border}` }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${T.border}` }}>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold" style={{ color: T.text }}>Set cover · {project.name}</div>
            <div className="text-xs" style={{ color: T.text2 }}>Upload a photo or pick one already in this project</div>
          </div>
          <button data-testid="cover-picker-close" onClick={onClose} className="ml-3 rounded-full px-2 text-lg leading-none" style={{ color: T.text2 }}>×</button>
        </div>

        <div className="px-5 py-4">
          <label data-testid="cover-picker-upload" className="inline-flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold" style={{ background: T.goldGrad, color: T.onGold, opacity: busy ? 0.6 : 1 }}>
            {busy ? '⏳ Uploading…' : '⬆ Upload a new photo'}
            <input type="file" accept="image/*" disabled={busy} className="hidden" onChange={(e) => { onCoverUpload(project, e); onClose(); }} />
          </label>
        </div>

        <div className="overflow-y-auto px-5 pb-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: T.muted }}>From this project</div>
          {loading ? (
            <div className="py-8 text-center text-sm" style={{ color: T.text2 }}>Loading photos…</div>
          ) : photos.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: T.text2 }}>No photos in this project yet — upload one above.</div>
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {photos.map((p, i) => (
                <button key={`${p.url}-${i}`} data-testid={`cover-photo-${i}`} onClick={() => pick(p.url)} title={p.label || 'Use as cover'} className="group relative aspect-square overflow-hidden rounded-lg" style={{ border: `1px solid ${T.border}` }}>
                  <img src={resolveUrl(p.url)} alt={p.label || ''} loading="lazy" className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                  <span className="absolute inset-x-0 bottom-0 bg-black/55 px-1 py-0.5 text-[9px] text-white opacity-0 transition-opacity group-hover:opacity-100">Use as cover</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CoverPicker;
